import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

// Internal tools
import i18n from '@/i18n/SetupI18n'
import { useConfiguredServicesStore } from '@/stores/configuredServices.js'
import { updateRuntime } from '@/providers/runtime.js'
import { promiseWithResolvers } from '@/helpers'
import { getHttpClient, HTTP_CONTENT_TYPES } from '@/utilities/httpClients'
import { logInWithCredentials, logInWithCognito, logInWithGoogle, reAuthenticateWithJwt } from '@/utilities/megalithApi'
import vueQueryPluginOptions from '@/utilities/vueQueryPluginOptions.js'
// Integrations
import integrations from '@/third-party-integrations/integrations.js'
// Globals
import {
  ACCOUNT_TYPE_DIRECT_CUSTOMER,
  ACCOUNT_TYPE_MANAGED_ACCOUNT,
  ACCOUNT_TYPE_PARTNER,
  API_GATEWAY_CLIENT,
  CPQ_LITE_CLIENT,
  LOGIN_GOOGLE,
  MEGAPORT_ID,
  MEGAPORT_LAB_ID,
} from '@/Globals.js'
import { AuthenticationError } from '@/AuthHelpers'

export const useAuthStore = defineStore('auth', () => {
  // Login state
  const isLoggingIn = ref(false)
  const isLoggedIn = ref(false)
  // Google sign-in state
  const googleSignInReady = ref(false)
  // User data
  const userLoginPayload = ref(null)
  const userData = ref({})
  // Tokens
  const jwt = computed(() => userData.value.oAuthToken?.accessToken)
  const cpqLiteJwt = ref(null)
  // Account types
  const isDirectAccount = computed(() => userData.value.accountType === ACCOUNT_TYPE_DIRECT_CUSTOMER)
  const isMegaportAdminOrLabAccount = computed(() => [MEGAPORT_ID, MEGAPORT_LAB_ID].includes(userData.value.companyId))
  const isPartnerAccount = computed(() => userData.value.accountType === ACCOUNT_TYPE_PARTNER)
  const isNonMpDirectAccount = computed(() => isDirectAccount.value && !isMegaportAdminOrLabAccount.value)

  // Check for the required feature flags exists
  const hasFeatureFlag = featureFlag => {
    return userData.value.featureFlags?.includes(featureFlag)
  }

  const federateAccount = async email => {
    // Ensure a clean slate
    userData.value = {}
    localStorage.removeItem('_accessToken')

    try {
      const { data: federatedData } = await getHttpClient(API_GATEWAY_CLIENT).post(
        '/v2/auth/federate',
        { email },
        { headers: { 'Content-Type': HTTP_CONTENT_TYPES.JSON } },
      )

      return federatedData
    } catch (error) {
      console.error(`Failed to federate user with email ${email}`, error)
      throw error
    }
  }

  const handleSuccessfulLogin = loginData => {
    // Ensure a clean slate in case of cached login data
    userLoginPayload.value = null
    // Ensure accountManager is included in the user data to store
    if (!loginData.accountManager) {
      loginData.accountManager = { name: null, email: null }
    }
    // Store the user data in the store
    userData.value = loginData
    // Flag the user as logged in
    isLoggedIn.value = true
    isLoggingIn.value = false
    // Save the access token in local storage if present
    if (jwt.value) {
      localStorage.setItem('_accessToken', jwt.value)
    }
    // Register the user and login event with integrations
    integrations.sentry.setUser(loginData.personUid, loginData.accountType)
    integrations.segment.setUser(loginData)
    integrations.segment.trackEvent('login')
    // Notify the user of a successful login
    window.mpApp.$message.success({ message: i18n.t('authentication.login-success-message') })
    // Navigate to the home page
    window.mpApp.$router.push('/', () => {})
  }

  const checkAccountAccess = ({ accountType, companyEnforcedMFA, companyId, mfaEnabled, mfaReset }) => {
    // Managed accounts are not allowed to log in
    if (accountType === ACCOUNT_TYPE_MANAGED_ACCOUNT) {
      throw new AuthenticationError(i18n.t('authentication.managed-account-no-access'))
    }

    // Only Megaport Admins, Lab accounts, Direct Customers, and Partners are allowed to log in
    if (
      ![MEGAPORT_ID, MEGAPORT_LAB_ID].includes(companyId) &&
      ![ACCOUNT_TYPE_DIRECT_CUSTOMER, ACCOUNT_TYPE_PARTNER].includes(accountType)
    ) {
      throw new AuthenticationError(i18n.t('authentication.no-access'))
    }

    // MFA reset required
    if (mfaReset) {
      const message = i18n.t('authentication.mfa-reset-customer-portal', { portalUrl: window.clientPortalUrl })

      throw new AuthenticationError(message, 'warning', true)
    }

    // MFA is enforced but not enabled
    if (companyEnforcedMFA && !mfaEnabled) {
      const message = i18n.t('authentication.mfa-enforced-customer-portal', { portalUrl: window.clientPortalUrl })

      throw new AuthenticationError(message, 'warning', true)
    }
  }

  const cpqLiteLoginCallback = promiseWithResolvers()

  const logIntoCpqLite = async token => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      }
      const { access_token } = await getHttpClient(CPQ_LITE_CLIENT).request({ url: 'login', method: 'post', headers })

      cpqLiteJwt.value = access_token
      cpqLiteLoginCallback.resolve()
    } catch (error) {
      cpqLiteLoginCallback.reject(error)
      throw error
    }
  }

  const logIn = async (payload, setLoginState = true) => {
    // Flag the user as logging in
    isLoggingIn.value = true

    try {
      let accountData
      // Determine the login method
      if (Object.hasOwn(payload, 'network')) {
        // Drop `network` from the sent payload as it's internal to the FE
        const { network, ...ssoPayload } = payload
        // Login with Google or Cognito (don't restrict payload as we need to send OTP for 2FA enabled accounts)
        accountData = await (network === LOGIN_GOOGLE ? logInWithGoogle : logInWithCognito)(ssoPayload)
      } else if (Object.hasOwn(payload, 'accessToken')) {
        // Re-authenticate with JWT
        accountData = await reAuthenticateWithJwt(payload.accessToken)
      } else {
        // Default to logging in with credentials (letting the API handle invalid payload errors)
        accountData = await logInWithCredentials(payload)
      }

      // Check whether the user has access to the Quoting Tool
      checkAccountAccess(accountData)

      // Update the runtime configuration
      updateRuntime(accountData)

      if (setLoginState) {
        // Log into CPQLite
        await logIntoCpqLite(accountData.oAuthToken?.accessToken)
        // Handle successful login side effects
        handleSuccessfulLogin(accountData)
      } else {
        userLoginPayload.value = payload
      }

      return accountData
    } catch (error) {
      userLoginPayload.value = payload
      isLoggedIn.value = false
      isLoggingIn.value = false
      // Remove the access token from local storage
      localStorage.removeItem('_accessToken')

      throw error
    }
  }

  const logOut = async () => {
    await getHttpClient(API_GATEWAY_CLIENT).get('/v2/logout')

    // Register the logout event with integrations
    integrations.segment.clearUser()
    // Remove the access token from local storage
    localStorage.removeItem('_accessToken')
    // Reset the store state
    userData.value = {}
    isLoggedIn.value = false
    cpqLiteJwt.value = null

    const { clearConfigurations } = useConfiguredServicesStore()
    clearConfigurations()

    // Clear the cache from Vue Query
    const { queryClient } = vueQueryPluginOptions
    queryClient.clear()
  }

  const handleSessionTimeout = () => {
    logOut()

    window.mpApp.$notify.error({
      title: window.mpApp.$t('authentication.failed'),
      message: window.mpApp.$t('authentication.login-failed-token'),
      duration: 10000,
    })

    window.mpApp.$router.push('/login', () => {})
  }

  return {
    isLoggingIn,
    isLoggedIn,
    googleSignInReady,
    userLoginPayload,
    userData,
    jwt,
    cpqLiteJwt,
    isDirectAccount,
    isMegaportAdminOrLabAccount,
    isPartnerAccount,
    isNonMpDirectAccount,
    cpqLiteLoginCallback,
    federateAccount,
    logIntoCpqLite,
    logIn,
    logOut,
    handleSessionTimeout,
    hasFeatureFlag,
  }
})

// Enable Hot Module Replacement
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
